import { useEffect, useMemo, useState } from 'react';
import styles from './PACSModalContent.module.scss';

import { PACS_CONTENTS_NAMES } from 'config/PACSConfig';
import {
  IMPORT_PACS_STUDY_COLUMN,
  IMPORT_PACS_SERIES_COLUMN,
} from 'config/tableColumns';
import { PacsParams } from '@models/pacs';
import { UsePacsRetrieveProps } from '@hooks/PACS/usePacs';
import { ICON_MAP } from '@components/Icons/Icons';

interface PACSModalContentProps {
  onSeriesFetch: (query_info: PacsParams) => void;
  onRetrieveSeries: (query_info: UsePacsRetrieveProps) => Promise<void> | void;
  isRetrievingSeries?: boolean;
  isFetchingStudy?: boolean;
  hasStudyQuerySubmitted?: boolean;
  study_data: any | null;
  series_data: any | null;
  dicomNode_data: string | undefined;
  onClose: () => void;
}

type PacsColumn = {
  id: string;
  label: string;
  sortable?: boolean;
};

const readRows = (source: any | null): any[] => {
  if (!source || !Array.isArray(source.data)) return [];
  return source.data;
};


const STUDY_COLUMN_WIDTHS: Record<string, string> = {
  patientID: '13%',
  patientName: '16%',
  patientSex: '9%',
  patientBirthDate: '12%',
  studyDate: '12%',
  studyID: '12%',
  accessionNumber: '15%',
  referringPhysicianName: '11%',
};

const SERIES_COLUMN_WIDTHS: Record<string, string> = {
  numberOfSeriesRelatedInstances: '13%',
  modality: '13%',
  patientID: '17%',
  patientName: '17%',
  seriesDate: '15%',
  seriesDesc: '25%',
};

const renderColGroup = (columns: PacsColumn[], widths: Record<string, string>) => (
  <colgroup>
    {columns.map(({ id }) => (
      <col key={id} style={{ width: widths[id] ?? `${100 / columns.length}%` }} />
    ))}
  </colgroup>
);

const PACSModalContent = ({
  onSeriesFetch,
  onRetrieveSeries,
  isRetrievingSeries = false,
  isFetchingStudy = false,
  hasStudyQuerySubmitted = false,
  study_data = null,
  series_data = null,
  dicomNode_data,
  onClose,
}: PACSModalContentProps) => {
  const [selectedStudy, setSelectedStudy] = useState<string>('');
  const [hoveredStudy, setHoveredStudy] = useState<string>('');
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [hoveredSeries, setHoveredSeries] = useState<string>('');
  const CloseIcon = ICON_MAP['close'];

  const studyRows = useMemo(() => readRows(study_data), [study_data]);
  const seriesRows = useMemo(() => readRows(series_data), [series_data]);
  const showStudyEmptyMessage =
    hasStudyQuerySubmitted && !isFetchingStudy && studyRows.length === 0;

  useEffect(() => {
    const newFormData: PacsParams = {
      SelectServer: dicomNode_data ?? '',
      PatientName: '',
      PatientID: '',
      BirthDate: '',
      AccessionNumber: '',
      Description: '',
      ReferringPhysician: '',
      StudyID: '',
      StudyUID: selectedStudy,
      SeriesUID: '',
      StudyDate: '',
      Modality: '',
    };
    onSeriesFetch(newFormData);
  }, [selectedStudy]);

  const handleRetrieve = () => {
    if (isRetrievingSeries) return;
    if (!selectedSeries) return;
    if (!dicomNode_data) return;
    const newFormData: UsePacsRetrieveProps = {
      dicomNode: dicomNode_data,
      seriesUID: selectedSeries,
    };
    onRetrieveSeries(newFormData);
  };

  useEffect(() => {
    setSelectedStudy('');
    setHoveredStudy('');
    setSelectedSeries('');
    setHoveredSeries('');
  }, [study_data]);

  const renderHeader = (columns: PacsColumn[]) => (
    <thead>
      <tr>
        {columns.map(({ id, label }) => (
          <th key={id} title={label}>
            <span>{label}</span>
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderCell = (row: any, id: string) => {
    const value = row[id] ?? '';
    const textValue = String(value);
    return (
      <td key={id} title={textValue}>
        <span>{textValue}</span>
      </td>
    );
  };

  const renderStudyTable = () => (
    <div className={styles.tableScroll}>
      <table className={`${styles.pacsTable} ${styles.studyTable}`}>
        {renderColGroup(IMPORT_PACS_STUDY_COLUMN, STUDY_COLUMN_WIDTHS)}
        {renderHeader(IMPORT_PACS_STUDY_COLUMN)}
        <tbody onMouseLeave={() => setHoveredStudy('')}>
          {studyRows.map((row, index) => {
            const uid = String(row.studyUID ?? index);
            const isHovered = uid === hoveredStudy;
            const isSelected = uid === selectedStudy;
            return (
              <tr
                key={uid}
                className={`${isHovered ? styles.hovered : ''} ${
                  isSelected ? styles.selected : ''
                }`}
                onMouseOver={() => setHoveredStudy(uid !== selectedStudy ? uid : '')}
                onClick={() => {
                  setSelectedStudy(selectedStudy === uid ? '' : uid);
                  setSelectedSeries('');
                }}
              >
                {IMPORT_PACS_STUDY_COLUMN.map(({ id }) => renderCell(row, id))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showStudyEmptyMessage && (
        <div className={styles.emptyMessage}>No matching studies found.</div>
      )}
    </div>
  );

  const renderSeriesTable = () => (
    <div className={styles.tableScroll}>
      <table className={`${styles.pacsTable} ${styles.seriesTable}`}>
        {renderColGroup(IMPORT_PACS_SERIES_COLUMN, SERIES_COLUMN_WIDTHS)}
        {renderHeader(IMPORT_PACS_SERIES_COLUMN)}
        <tbody onMouseLeave={() => setHoveredSeries('')}>
          {seriesRows.map((row, index) => {
            const uid = String(row.seriesUID ?? index);
            const isHovered = uid === hoveredSeries;
            const isSelected = uid === selectedSeries;
            return (
              <tr
                key={uid}
                className={`${isHovered ? styles.hovered : ''} ${
                  isSelected ? styles.selected : ''
                }`}
                onMouseOver={() => setHoveredSeries(uid !== selectedSeries ? uid : '')}
                onClick={() => setSelectedSeries(uid)}
              >
                {IMPORT_PACS_SERIES_COLUMN.map(({ id }) => renderCell(row, id))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles['label-input']}>
          <div className={styles.label}>
            <div className={styles.label2}>
              <span>{PACS_CONTENTS_NAMES.PACS.pacsstudylist}</span>
              <div className={styles.itemclose}>
                <CloseIcon className={styles.closeIcon} onClick={onClose} />
              </div>
            </div>
          </div>
        </div>
        <div className={styles['study-list']}>{renderStudyTable()}</div>

        <div className={styles['label-input']}>
          <div className={styles.label}>
            <div className={styles.label2}>
              <span>{PACS_CONTENTS_NAMES.PACS.pacserieslist}</span>
            </div>
          </div>
        </div>
        <div className={styles['study-list']}>{renderSeriesTable()}</div>
      </div>

      <div className={styles.actions}>
        <button onClick={onClose}>{PACS_CONTENTS_NAMES.PACS.close}</button>
        <button
          className={styles.downButton}
          onClick={handleRetrieve}
          disabled={isRetrievingSeries || !selectedSeries.length}
        >
          {PACS_CONTENTS_NAMES.PACS.download}
        </button>
      </div>
    </div>
  );
};

export default PACSModalContent;
